import axios from "axios";

import {
  calculatePaymentRemainingAmount,
  normalizeMoneyInput,
} from "./contractDetailsShared.mjs";

const TERMINAL_CONTRACT_STATUSES = ["terminated", "canceled", "expired"];
const TERMINAL_BILLING_RESOLUTIONS = ["keep_receivables", "cancel_receivables"];

export const createTerminalBillingState = () => ({
  terminalBillingDialog: false,
  terminalBillingSaving: false,
  terminalBillingTargetStatus: "",
  terminalBillingResolution: "keep_receivables",
  terminalBillingImpact: null,
});

export const terminalBillingComputed = {
  terminalBillingClientImpact() {
    const activeBillingPlanItems = this.billingPlanItems.filter((item) => {
      const status = String(item?.status || "").trim().toLowerCase();
      return status === "draft" || status === "active";
    });
    const openPaymentItems = this.paymentItems.filter((item) => {
      const status = String(item?.status || "").trim().toLowerCase();
      return ["pending", "partial", "overdue"].includes(status) && calculatePaymentRemainingAmount(item) > 0;
    });
    const moneySum = (rows) => rows.reduce((total, item) => {
      return Math.round((total + calculatePaymentRemainingAmount(item)) * 100) / 100;
    }, 0);
    const overduePaymentItems = openPaymentItems.filter((item) => String(item?.status || "").toLowerCase() === "overdue");
    const partialPaymentItems = openPaymentItems.filter((item) => (normalizeMoneyInput(item?.amount_paid) ?? 0) > 0);
    const nextDueAt = openPaymentItems
      .map((item) => String(item?.due_at || "").trim())
      .filter(Boolean)
      .sort((left, right) => left.localeCompare(right))[0] || null;
    return {
      requires_resolution: activeBillingPlanItems.length > 0 || openPaymentItems.length > 0,
      active_billing_plan_count: activeBillingPlanItems.length,
      open_payment_count: openPaymentItems.length,
      cancelable_payment_count: openPaymentItems.length - partialPaymentItems.length,
      partial_payment_count: partialPaymentItems.length,
      overdue_payment_count: overduePaymentItems.length,
      open_amount: moneySum(openPaymentItems),
      overdue_amount: moneySum(overduePaymentItems),
      next_due_at: nextDueAt,
    };
  },
  terminalBillingDialogImpact() {
    return this.terminalBillingImpact || this.terminalBillingClientImpact;
  },
};

export const terminalBillingMethods = {
  isTerminalContractStatus(status) {
    return TERMINAL_CONTRACT_STATUSES.includes(String(status || "").trim().toLowerCase());
  },
  shouldPromptTerminalBillingStatus(status) {
    return this.isTerminalContractStatus(status) && this.terminalBillingClientImpact.requires_resolution === true;
  },
  openTerminalBillingDialog(status, impact = null) {
    this.terminalBillingTargetStatus = String(status || "").trim().toLowerCase();
    this.terminalBillingImpact = impact || null;
    this.terminalBillingResolution = "keep_receivables";
    this.terminalBillingDialog = true;
    this.statusMenu = false;
  },
  handleTerminalBillingDecisionRequired(error, status) {
    const code = String(error?.response?.data?.code || "").trim();
    if (code !== "TERMINAL_BILLING_DECISION_REQUIRED") return false;
    this.openTerminalBillingDialog(status, error?.response?.data?.details?.billing || null);
    return true;
  },
  async submitContractStatusUpdate(normalizedNextStatus, extraPayload = {}) {
    this.statusSaving = true;
    try {
      const { data } = await axios.patch(`/ncrm/contracts/${this.normalizedContractId}/status`, {
        status: normalizedNextStatus,
        ...extraPayload,
      });
      this.contract = data?.contract || this.contract;
      this.statusMenu = false;
      this.terminalBillingDialog = false;
      this.terminalBillingTargetStatus = "";
      this.terminalBillingImpact = null;
      if (!(this.contract?.fulfillment_state?.can_mark_fulfilled === true)) {
        this.fulfillmentDialog = false;
      }
      this.showSnackbar(
        this.$t("ncrm.contracts.messages.statusUpdated", {
          status: this.statusLabel(normalizedNextStatus),
        }),
        "success"
      );
      await this.ensureBillingSetupState();
    } catch (error) {
      console.error("[ncrm-contract-details] update status failed", error);
      if (this.handleTerminalBillingDecisionRequired(error, normalizedNextStatus)) {
        return;
      }
      this.showSnackbar(
        error?.response?.data?.error || this.$t("ncrm.contracts.messages.statusUpdateError"),
        "error"
      );
    } finally {
      this.statusSaving = false;
    }
  },
  async updateContractStatus(nextStatus) {
    if (!this.normalizedContractId || !this.contract) return;
    const normalizedNextStatus = String(nextStatus || "").trim().toLowerCase();
    if (!normalizedNextStatus) return;
    if (normalizedNextStatus === this.normalizedContractStatus) {
      this.statusMenu = false;
      return;
    }
    if (normalizedNextStatus === "fulfilled" && !this.canSelectFulfilledStatus) {
      return;
    }
    if (this.shouldPromptTerminalBillingStatus(normalizedNextStatus)) {
      this.openTerminalBillingDialog(normalizedNextStatus);
      return;
    }
    await this.submitContractStatusUpdate(normalizedNextStatus);
  },
  async confirmTerminalBillingStatus() {
    const normalizedNextStatus = String(this.terminalBillingTargetStatus || "").trim().toLowerCase();
    const resolution = String(this.terminalBillingResolution || "").trim().toLowerCase();
    if (!normalizedNextStatus || !TERMINAL_BILLING_RESOLUTIONS.includes(resolution)) return;
    this.terminalBillingSaving = true;
    try {
      await this.submitContractStatusUpdate(normalizedNextStatus, {
        billing_resolution: resolution,
      });
    } finally {
      this.terminalBillingSaving = false;
    }
  },
};
